// CareLogix™ ORACLE — Acknowledgement Gate client (server-backed, versioned).
// Drives the ORACLE_ACKNOWLEDGEMENT_GATE™ + ORACLE_ACKNOWLEDGEMENT_PERSISTENCE™ +
// ORACLE_VERSIONED_DISCLOSURES™ flow. Bumping ORACLE_CURRENT_VERSION_HINT in
// the backend (oracle_acknowledgement.py) forces all users to re-acknowledge.
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// LocalStorage keys (additive; coexist with v1 from Phase 32.1)
export const ORACLE_ACK_V3_KEY      = "oracle_acknowledged_v3";
export const ORACLE_ACK_V1_KEY      = "oracle_acknowledged_v1";
export const ORACLE_ACK_VERSION_KEY = "oracle_acknowledged_version";
export const ORACLE_DEVICE_ID_KEY   = "oracle_device_id";
export const ORACLE_LAST_CHECK_KEY  = "oracle_acknowledged_last_check_ts";
// Session-storage key used by ORACLE_ALWAYS_ACKNOWLEDGE™ to mark the current
// tab session as already acknowledged. Was previously referenced without being
// declared, causing a ReferenceError in isAcknowledgedThisSession().
export const ORACLE_ACK_SESSION_KEY = "oracle_acknowledged_session_v3";

const RECHECK_MS = 5 * 60 * 1000;          // re-check server every 5 minutes
const FAST_PATH_GRACE_MS = 60 * 1000;      // trust localStorage for 60s after server confirms

// ─── Stable device id for anonymous users ───────────────────────────────────
function genUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // RFC4122 v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceId() {
  if (typeof window === "undefined") return "anon-ssr";
  try {
    let id = window.localStorage.getItem(ORACLE_DEVICE_ID_KEY);
    if (!id) {
      id = `dev-${genUuid()}`;
      window.localStorage.setItem(ORACLE_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `dev-${genUuid()}`;
  }
}

// ─── Resolve user_id: prefer signed-in member, fallback to AdminOne, then device id ──
export function resolveUserId(scope = "public") {
  if (typeof window === "undefined") return "anon-ssr";
  try {
    if (scope === "adminone") {
      const adminRaw = window.localStorage.getItem("carelogix_adminone_user");
      if (adminRaw) {
        try {
          const u = JSON.parse(adminRaw);
          if (u?.email) return `adminone:${u.email}`;
        } catch { /* ignore */ }
      }
      return `adminone:${getDeviceId()}`;
    }
    // public scope — prefer ORACLE member auth if present
    const memberRaw = window.localStorage.getItem("oracle_member_user") || window.localStorage.getItem("oracle_member") || "";
    if (memberRaw) {
      try {
        const m = JSON.parse(memberRaw);
        if (m?.id || m?.email) return `member:${m.id || m.email}`;
      } catch { /* ignore */ }
    }
    return getDeviceId();
  } catch {
    return getDeviceId();
  }
}

// ─── localStorage fast-path ────────────────────────────────────────────────
export function isAcknowledgedV3Local(currentVersion) {
  if (typeof window === "undefined") return false;
  try {
    const ackFlag = window.localStorage.getItem(ORACLE_ACK_V3_KEY) === "true";
    if (!ackFlag) {
      // Soft-migrate from v1 if user previously acked under Phase 32.1
      if (window.localStorage.getItem(ORACLE_ACK_V1_KEY) === "true") {
        window.localStorage.setItem(ORACLE_ACK_V3_KEY, "true");
      } else {
        return false;
      }
    }
    // Version match required
    const localVersion = window.localStorage.getItem(ORACLE_ACK_VERSION_KEY);
    if (currentVersion && localVersion && localVersion !== currentVersion) return false;
    return true;
  } catch { return false; }
}

export function setAcknowledgedV3Local(value, version) {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      window.localStorage.setItem(ORACLE_ACK_V3_KEY, "true");
      if (version) window.localStorage.setItem(ORACLE_ACK_VERSION_KEY, version);
      window.localStorage.setItem(ORACLE_LAST_CHECK_KEY, String(Date.now()));
    } else {
      window.localStorage.removeItem(ORACLE_ACK_V3_KEY);
      window.localStorage.removeItem(ORACLE_ACK_VERSION_KEY);
    }
  } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent("oracle:acknowledged-v3-changed", { detail: { value: !!value, version } }));
  } catch { /* ignore */ }
}

// ─── Server endpoints ──────────────────────────────────────────────────────
export async function fetchCurrentVersion() {
  const r = await axios.get(`${BACKEND}/api/oracle/acknowledgement/current-version`);
  return r.data?.current_version || "ORACLE_V1";
}

export async function fetchConfig() {
  // Returns { current_version, always_acknowledge, collection }
  const r = await axios.get(`${BACKEND}/api/oracle/acknowledgement/config`);
  return r.data || { current_version: "ORACLE_V1", always_acknowledge: true };
}

export async function fetchServerStatus(userId, scope = "public") {
  const r = await axios.get(`${BACKEND}/api/oracle/acknowledgement/status`, { params: { user_id: userId, scope } });
  return r.data;
}

export async function acceptServer(userId, scope = "public", version = null) {
  const body = { user_id: userId, scope };
  if (version) body.version = version;
  const r = await axios.post(`${BACKEND}/api/oracle/acknowledgement/accept`, body);
  return r.data;
}

// ─── Session marker (for ORACLE_ALWAYS_ACKNOWLEDGE™) ──────────────────────
export function isAcknowledgedThisSession() {
  if (typeof window === "undefined") return false;
  try { return window.sessionStorage.getItem(ORACLE_ACK_SESSION_KEY) === "true"; }
  catch { return false; }
}

export function setAcknowledgedThisSession(v) {
  if (typeof window === "undefined") return;
  try {
    if (v) window.sessionStorage.setItem(ORACLE_ACK_SESSION_KEY, "true");
    else   window.sessionStorage.removeItem(ORACLE_ACK_SESSION_KEY);
  } catch { /* ignore */ }
}

// ─── Combined check: localStorage fast-path, else server-of-truth ──────────
//
// Returns: { acknowledged, version, source, userId, alwaysAcknowledge }
//
// When server config says `always_acknowledge: true`:
//   - localStorage fast-path is IGNORED
//   - the user must accept in *this* session (tracked via sessionStorage)
//   - localStorage + Mongo are still updated for audit trail
//
export async function checkAcknowledgement(scope = "public") {
  const userId = resolveUserId(scope);
  // Step 1: fetch live config (current version + always_acknowledge flag)
  let currentVersion = "ORACLE_V1";
  let alwaysAcknowledge = true;
  let serverReachable = false;
  try {
    const cfg = await fetchConfig();
    currentVersion = cfg.current_version || "ORACLE_V1";
    alwaysAcknowledge = Boolean(cfg.always_acknowledge);
    serverReachable = true;
  } catch { /* offline → trust local with last-known version, assume always_ack TRUE per directive */ }

  // Step 2: ORACLE_ALWAYS_ACKNOWLEDGE™ short-circuit — session-only marker
  if (alwaysAcknowledge) {
    const inSession = isAcknowledgedThisSession();
    return {
      acknowledged: inSession,
      version: currentVersion,
      source: inSession ? "session" : "always-ack-required",
      userId,
      alwaysAcknowledge: true,
    };
  }

  // Step 3: localStorage fast path (only valid when always_ack is FALSE)
  if (typeof window !== "undefined") {
    try {
      const lastCheckRaw = window.localStorage.getItem(ORACLE_LAST_CHECK_KEY);
      const lastCheck = lastCheckRaw ? Number(lastCheckRaw) : 0;
      const fresh = lastCheck && (Date.now() - lastCheck) < FAST_PATH_GRACE_MS;
      const localOk = isAcknowledgedV3Local(currentVersion);
      if (!serverReachable && localOk) {
        return { acknowledged: true, version: currentVersion, source: "local-offline", userId, alwaysAcknowledge };
      }
      if (localOk && fresh) {
        return { acknowledged: true, version: currentVersion, source: "local-fast", userId, alwaysAcknowledge };
      }
    } catch { /* ignore */ }
  }

  // Step 4: server check
  if (serverReachable) {
    try {
      const s = await fetchServerStatus(userId, scope);
      if (s?.acknowledged_for_current_version) {
        setAcknowledgedV3Local(true, s.current_version);
        return { acknowledged: true, version: s.current_version, source: "server", userId, alwaysAcknowledge };
      }
      setAcknowledgedV3Local(false);
      return { acknowledged: false, version: s?.current_version || currentVersion, source: "server", userId, alwaysAcknowledge };
    } catch { /* server hiccup */ }
  }

  // Step 5: final fallback to localStorage best-effort
  const localOk = isAcknowledgedV3Local(currentVersion);
  return { acknowledged: localOk, version: currentVersion, source: localOk ? "local-fallback" : "none", userId, alwaysAcknowledge };
}

// Compatibility shim for callers that imported the synchronous helper.
// Returns the localStorage answer for the *last known* version.
export function isAcknowledgedV3() {
  if (typeof window === "undefined") return true;
  try {
    const v = window.localStorage.getItem(ORACLE_ACK_VERSION_KEY) || null;
    return isAcknowledgedV3Local(v);
  } catch { return false; }
}

export const RECHECK_INTERVAL_MS = RECHECK_MS;
